'use client';

import { Button } from '@heidi/design-system/button';
import { TypographyCaptionBold } from '@heidi/design-system/typography';
import { ClinicalTaskStatus } from '@heidi/schema/ml-scribe';
import { Check, Circle, History } from 'lucide-react';
import * as m from 'motion/react-m';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

interface ClinicalTaskStatusToggleProps {
  status: ClinicalTaskStatus;
  taskId: string;
  onToggle: (taskId: string, status: ClinicalTaskStatus) => void;
  disabled?: boolean;
  onRestore?: (taskId: string) => void;
}

export function ClinicalTaskStatusToggle({
  status,
  taskId,
  onToggle,
  disabled = false,
  onRestore,
}: ClinicalTaskStatusToggleProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState(status);

  // Update optimistic status when prop changes (for external updates)
  useEffect(() => {
    if (!isAnimating) {
      setOptimisticStatus(status);
    }
  }, [status, isAnimating]);

  const handleToggle = () => {
    if (disabled) return;
    setIsAnimating(true);

    const newStatus =
      status === ClinicalTaskStatus.done
        ? ClinicalTaskStatus.todo
        : ClinicalTaskStatus.done;
    setOptimisticStatus(newStatus);

    onToggle(taskId, status);

    setTimeout(() => {
      setIsAnimating(false);
    }, 300);
  };

  const displayStatus = optimisticStatus;

  if (status === ClinicalTaskStatus.archived) {
    if (onRestore) {
      return (
        <Button
          disableAutomaticSvgSizing
          variant="ghost"
          size="sm"
          className="m-0 flex items-center gap-1 p-0 pr-2 hover:bg-transparent"
          onClick={() => onRestore(taskId)}
          data-testid="clinical-task-restore-button"
        >
          <History className="size-3.5 text-text-secondary" />
          <TypographyCaptionBold className="text-text-secondary">
            <FormattedMessage
              defaultMessage="Restore"
              id="fztMk1cUHD"
              description="Button that restores an archived clinical task"
            />
          </TypographyCaptionBold>
        </Button>
      );
    } else {
      return (
        <div className="m-0 h-fit min-w-0 px-0 opacity-40">
          <Circle className="size-3.5 text-text-disabled" />
        </div>
      );
    }
  }

  return (
    <Button
      disableAutomaticSvgSizing
      variant="ghost"
      size="sm"
      className="m-0 h-fit min-w-0 p-0"
      disabled={disabled}
      onClick={handleToggle}
      data-testid="clinical-task-status-toggle"
    >
      <m.div
        animate={{ scale: isAnimating ? [1, 1.2, 1] : 1 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        {displayStatus === ClinicalTaskStatus.done ? (
          <m.div
            animate={{ scale: isAnimating ? [1, 1.1, 1] : 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <m.div
              className="rounded-full bg-validation-success p-0.5"
              initial={false}
              animate={{
                scale: isAnimating ? [1, 1.1, 1] : 1,
                boxShadow: isAnimating
                  ? [
                      '0 0 0 0px rgba(34, 197, 94, 0.7)',
                      '0 0 0 6px rgba(34, 197, 94, 0)',
                      '0 0 0 0px rgba(34, 197, 94, 0)',
                    ]
                  : '0 0 0 0px rgba(34, 197, 94, 0)',
              }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <Check className="size-2.5 text-text-primary-invert" />
            </m.div>
          </m.div>
        ) : (
          <m.div
            animate={{ scale: isAnimating ? [1, 0.9, 1] : 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <Circle className="size-3.5 text-text-tertiary" />
          </m.div>
        )}
      </m.div>
    </Button>
  );
}
